import {
  type CmsRow,
  type RateScheduleDraft as Draft,
  type MMRate,
  type RateBand,
  type YearRate,
  bandRow,
  buildRateScheduleConfig as buildConfig,
  cmsRow,
  draftFromConfigRow as draftFromRow,
  emptyRateScheduleDraft as emptyDraft,
  mmRow,
  summarizeRateSchedule as summarize,
  yrRow,
} from '@commissionsight/shared';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  type ProductLine,
  type RateScheduleRow,
  createRateSchedule,
  getCarriers,
  getRateSchedules,
  getWorkspaces,
  updateRateSchedule,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { Badge } from './ui.js';

const LINE_HELP: Record<ProductLine, string> = {
  major_medical:
    '% of premium or flat/PMPM per member — with optional graded tiers (premium bands, first-year/renewal) and a production bonus.',
  medicare: 'CMS advance-and-earn — set the maximum-compensation table by year/product.',
  ancillary: 'Premium-based — set policy-year rate bands for the product family.',
};
const tone: Record<ProductLine, 'sky' | 'indigo' | 'amber'> = {
  major_medical: 'sky',
  medicare: 'indigo',
  ancillary: 'amber',
};

const inp = 'w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm';
const smallBtn =
  'rounded-md border border-neutral-300 px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-50';
type SetDraft = Dispatch<SetStateAction<Draft>>;

/**
 * Structured editor for an account's commission rate schedules — no raw JSON.
 * Pick a product line, scope it (workspace/carrier), and fill in the table; the
 * config is built + sent to the API, which validates it. The pure draft↔config
 * logic lives in @commissionsight/shared (`rate-schedule-form`), unit-tested there.
 */
export function RateSchedulesPanel({ accountId }: { accountId: string }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => getRateSchedules(accountId), [accountId]),
  );
  const [workspaces, setWorkspaces] = useState<{ id: string; name: string }[]>([]);
  const [wsEnabled, setWsEnabled] = useState(false);
  const [carriers, setCarriers] = useState<{ id: string; name: string }[]>([]);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editId, setEditId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    getWorkspaces(accountId)
      .then((w) => {
        setWorkspaces(w.workspaces.map((x) => ({ id: x.id, name: x.name })));
        setWsEnabled(w.workspacesEnabled);
      })
      .catch(() => {});
    getCarriers()
      .then(setCarriers)
      .catch(() => {});
  }, [accountId]);

  const carrierName = (id: string | null) =>
    id ? (carriers.find((c) => c.id === id)?.name ?? id.slice(0, 8)) : 'All carriers';
  const wsName = (id: string | null) =>
    id ? (workspaces.find((w) => w.id === id)?.name ?? id.slice(0, 8)) : 'All workspaces';

  const startAdd = () => {
    setDraft(emptyDraft());
    setEditId(null);
    setMsg(null);
    setOpen(true);
  };
  const startEdit = (row: RateScheduleRow) => {
    setDraft(draftFromRow(row));
    setEditId(row.id);
    setMsg(null);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const config = buildConfig(draft);
      if (editId) await updateRateSchedule(accountId, editId, { config });
      else
        await createRateSchedule(accountId, {
          workspaceId: draft.workspaceId || null,
          carrierId: draft.carrierId || null,
          config,
        });
      setOpen(false);
      await reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (s: RateScheduleRow) => {
    setBusy(true);
    try {
      await updateRateSchedule(accountId, s.id, { isActive: !s.isActive });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <span className="text-xs text-rose-600">Couldn’t load rate schedules</span>;

  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Commission rate schedules
        </span>
        {!open && (
          <button
            type="button"
            onClick={startAdd}
            className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            + Add schedule
          </button>
        )}
      </div>

      {!data ? (
        <span className="text-xs text-neutral-400">Loading…</span>
      ) : data.length === 0 ? (
        <p className="text-sm text-neutral-500">
          No schedules yet — add one so commission owed can be computed.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-neutral-500">
              <th className="py-1">Product line</th>
              <th className="py-1">Scope</th>
              <th className="py-1">Schedule</th>
              <th className="py-1">Status</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {data.map((s) => (
              <tr key={s.id} className="border-t border-neutral-100 align-top">
                <td className="py-1.5">
                  <Badge tone={tone[s.productLine]}>{s.productLine}</Badge>
                </td>
                <td className="py-1.5 text-xs text-neutral-500">
                  {wsEnabled ? `${wsName(s.workspaceId)} · ` : ''}
                  {carrierName(s.carrierId)}
                </td>
                <td className="py-1.5 text-xs text-neutral-600">{summarize(s)}</td>
                <td className="py-1.5">
                  <Badge tone={s.isActive ? 'emerald' : 'neutral'}>
                    {s.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => startEdit(s)}
                    className="mr-3 text-xs font-medium text-emerald-700 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => toggle(s)}
                    className="text-xs font-medium text-neutral-600 hover:underline disabled:opacity-50"
                  >
                    {s.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className="mt-3 rounded-lg border border-neutral-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold">
              {editId ? 'Edit schedule' : 'New schedule'}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-neutral-500 hover:text-neutral-800"
            >
              Cancel
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">Product line</span>
              <select
                className={inp}
                value={draft.line}
                disabled={!!editId}
                onChange={(e) => setDraft((d) => ({ ...d, line: e.target.value as ProductLine }))}
              >
                <option value="major_medical">Major medical</option>
                <option value="medicare">Medicare</option>
                <option value="ancillary">Ancillary</option>
              </select>
            </label>
            {wsEnabled && (
              <label className="text-xs">
                <span className="mb-1 block text-neutral-500">Workspace</span>
                <select
                  className={inp}
                  value={draft.workspaceId}
                  disabled={!!editId}
                  onChange={(e) => setDraft((d) => ({ ...d, workspaceId: e.target.value }))}
                >
                  <option value="">All workspaces</option>
                  {workspaces.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">Carrier</span>
              <select
                className={inp}
                value={draft.carrierId}
                disabled={!!editId}
                onChange={(e) => setDraft((d) => ({ ...d, carrierId: e.target.value }))}
              >
                <option value="">All carriers</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="mt-1.5 text-[11px] text-neutral-400">{LINE_HELP[draft.line]}</p>

          <div className="mt-3">
            {draft.line === 'major_medical' && (
              <MajorMedicalForm draft={draft} setDraft={setDraft} />
            )}
            {draft.line === 'medicare' && <MedicareForm draft={draft} setDraft={setDraft} />}
            {draft.line === 'ancillary' && <AncillaryForm draft={draft} setDraft={setDraft} />}
          </div>

          {msg && <p className="mt-2 text-xs text-rose-600">{msg}</p>}
          <button
            type="button"
            disabled={busy}
            onClick={save}
            className="mt-3 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {editId ? 'Save changes' : 'Add schedule'}
          </button>
        </div>
      )}
    </div>
  );
}

function RowActions({ onAdd, onRemove }: { onAdd: () => void; onRemove?: () => void }) {
  return (
    <div className="flex items-end gap-2 pb-0.5">
      {onRemove && (
        <button type="button" className={smallBtn} onClick={onRemove}>
          Remove
        </button>
      )}
      <button type="button" className={smallBtn} onClick={onAdd}>
        + Row
      </button>
    </div>
  );
}

function MajorMedicalForm({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const set = (i: number, patch: Partial<MMRate>) =>
    setDraft((d) => ({ ...d, mm: d.mm.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const overridePercent =
    draft.mmOverrideMethod === 'percent_of_premium' ||
    draft.mmOverrideMethod === 'percent_of_commission';
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-medium text-neutral-500">
        Rate tiers (blank plan = carrier default; add a premium band or first-year/renewal to
        grade).
      </div>
      {draft.mm.map((r, i) => (
        <div key={r.uid} className="rounded-lg border border-neutral-200 p-2.5">
          <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">Plan code</span>
              <input
                className={inp}
                value={r.planCode}
                placeholder="all plans"
                onChange={(e) => set(i, { planCode: e.target.value })}
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">Basis</span>
              <select
                className={inp}
                value={r.rateType}
                onChange={(e) => set(i, { rateType: e.target.value as MMRate['rateType'] })}
              >
                <option value="percent_of_premium">% of premium</option>
                <option value="flat_per_member">Flat / PMPM per member</option>
              </select>
            </label>
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">
                {r.rateType === 'percent_of_premium' ? 'Percent' : 'Dollars'}
              </span>
              <input
                className={inp}
                type="number"
                min="0"
                step="any"
                value={r.rateValue}
                onChange={(e) => set(i, { rateValue: e.target.value })}
              />
            </label>
            <RowActions
              onAdd={() => setDraft((d) => ({ ...d, mm: [...d.mm, mmRow()] }))}
              onRemove={
                draft.mm.length > 1
                  ? () => setDraft((d) => ({ ...d, mm: d.mm.filter((_, j) => j !== i) }))
                  : undefined
              }
            />
          </div>
          <div className="mt-2 grid items-end gap-2 sm:grid-cols-3">
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">Min premium (band, optional)</span>
              <input
                className={inp}
                type="number"
                min="0"
                step="any"
                value={r.minPremium}
                placeholder="—"
                onChange={(e) => set(i, { minPremium: e.target.value })}
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">Max premium (band, optional)</span>
              <input
                className={inp}
                type="number"
                min="0"
                step="any"
                value={r.maxPremium}
                placeholder="—"
                onChange={(e) => set(i, { maxPremium: e.target.value })}
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">Applies to</span>
              <select
                className={inp}
                value={r.compensationType}
                onChange={(e) =>
                  set(i, { compensationType: e.target.value as MMRate['compensationType'] })
                }
              >
                <option value="">First-year &amp; renewal</option>
                <option value="initial">First-year only</option>
                <option value="renewal">Renewal only</option>
              </select>
            </label>
          </div>
        </div>
      ))}
      <div className="rounded-lg border border-neutral-200 p-2.5">
        <span className="text-[11px] font-medium text-neutral-500">
          Production bonus (optional)
        </span>
        <div className="mt-2 grid items-end gap-2 sm:grid-cols-2">
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Bonus type</span>
            <select
              className={inp}
              value={draft.mmOverrideMethod}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  mmOverrideMethod: e.target.value as Draft['mmOverrideMethod'],
                }))
              }
            >
              <option value="">None</option>
              <option value="percent_of_premium">% of premium</option>
              <option value="percent_of_commission">% of commission</option>
              <option value="flat_per_member">Flat per member</option>
              <option value="per_member_per_month">Per member / month (PMPM)</option>
            </select>
          </label>
          {draft.mmOverrideMethod && (
            <label className="text-xs">
              <span className="mb-1 block text-neutral-500">
                {overridePercent ? 'Bonus percent' : 'Bonus dollars'}
              </span>
              <input
                className={inp}
                type="number"
                min="0"
                step="any"
                value={draft.mmOverrideRate}
                onChange={(e) => setDraft((d) => ({ ...d, mmOverrideRate: e.target.value }))}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}

function MedicareForm({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const set = (i: number, patch: Partial<CmsRow>) =>
    setDraft((d) => ({ ...d, cms: d.cms.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-medium text-neutral-500">CMS max-compensation table</div>
      {draft.cms.map((r, i) => (
        <div
          key={r.uid}
          className="grid items-end gap-2 sm:grid-cols-[0.8fr_0.8fr_0.8fr_1fr_0.9fr_auto]"
        >
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Year</span>
            <input
              className={inp}
              type="number"
              value={r.contractYear}
              onChange={(e) => set(i, { contractYear: e.target.value })}
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Product</span>
            <select
              className={inp}
              value={r.productType}
              onChange={(e) => set(i, { productType: e.target.value as CmsRow['productType'] })}
            >
              <option>MA</option>
              <option>MAPD</option>
              <option>PDP</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Region</span>
            <input
              className={inp}
              value={r.region}
              placeholder="national"
              onChange={(e) => set(i, { region: e.target.value })}
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Comp type</span>
            <select
              className={inp}
              value={r.compensationType}
              onChange={(e) =>
                set(i, { compensationType: e.target.value as CmsRow['compensationType'] })
              }
            >
              <option value="initial">initial</option>
              <option value="renewal">renewal</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Annual $</span>
            <input
              className={inp}
              type="number"
              min="0"
              step="any"
              value={r.annualAmount}
              onChange={(e) => set(i, { annualAmount: e.target.value })}
            />
          </label>
          <RowActions
            onAdd={() => setDraft((d) => ({ ...d, cms: [...d.cms, cmsRow()] }))}
            onRemove={
              draft.cms.length > 1
                ? () => setDraft((d) => ({ ...d, cms: d.cms.filter((_, j) => j !== i) }))
                : undefined
            }
          />
        </div>
      ))}
      <label className="block w-40 text-xs">
        <span className="mb-1 block text-neutral-500">RDE months</span>
        <input
          className={inp}
          type="number"
          min="1"
          max="12"
          value={draft.rdeMonths}
          onChange={(e) => setDraft((d) => ({ ...d, rdeMonths: e.target.value }))}
        />
      </label>
      <label className="block text-xs">
        <span className="mb-1 block text-neutral-500">Base payment cadence</span>
        <select
          className={inp}
          value={draft.medicareCadence}
          onChange={(e) =>
            setDraft((d) => ({ ...d, medicareCadence: e.target.value as Draft['medicareCadence'] }))
          }
        >
          <option value="annual_upfront">Annual upfront (lump)</option>
          <option value="monthly">Monthly installments</option>
        </select>
      </label>
    </div>
  );
}

function YearRateRows({
  rows,
  onChange,
}: {
  rows: YearRate[];
  onChange: (rows: YearRate[]) => void;
}) {
  const set = (i: number, patch: Partial<YearRate>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={r.uid} className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">From year</span>
            <input
              className={inp}
              type="number"
              min="1"
              value={r.fromYear}
              onChange={(e) => set(i, { fromYear: e.target.value })}
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">To year (blank=open)</span>
            <input
              className={inp}
              type="number"
              min="1"
              value={r.toYear}
              placeholder="∞"
              onChange={(e) => set(i, { toYear: e.target.value })}
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Rate %</span>
            <input
              className={inp}
              type="number"
              min="0"
              step="any"
              value={r.rate}
              onChange={(e) => set(i, { rate: e.target.value })}
            />
          </label>
          <RowActions
            onAdd={() => onChange([...rows, yrRow()])}
            onRemove={rows.length > 1 ? () => onChange(rows.filter((_, j) => j !== i)) : undefined}
          />
        </div>
      ))}
    </div>
  );
}

function AncillaryForm({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const anyAge = draft.bands.some((b) => b.ageBand !== '');
  const setBand = (i: number, patch: Partial<RateBand>) =>
    setDraft((d) => ({ ...d, bands: d.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Product family</span>
          <input
            className={inp}
            value={draft.family}
            placeholder="med_supp"
            onChange={(e) => setDraft((d) => ({ ...d, family: e.target.value }))}
          />
        </label>
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Method</span>
          <select
            className={inp}
            value={draft.method}
            onChange={(e) => setDraft((d) => ({ ...d, method: e.target.value as Draft['method'] }))}
          >
            <option value="as_earned">As earned</option>
            <option value="advanced">Advanced</option>
          </select>
        </label>
        {draft.method === 'advanced' && (
          <label className="text-xs">
            <span className="mb-1 block text-neutral-500">Advance fraction</span>
            <input
              className={inp}
              type="number"
              min="0"
              max="1"
              step="any"
              value={draft.advanceFraction}
              onChange={(e) => setDraft((d) => ({ ...d, advanceFraction: e.target.value }))}
            />
          </label>
        )}
      </div>

      <label className="block text-xs">
        <span className="mb-1 block text-neutral-500">Rate structure</span>
        <select
          className={inp}
          value={draft.ancMode}
          onChange={(e) => setDraft((d) => ({ ...d, ancMode: e.target.value as Draft['ancMode'] }))}
        >
          <option value="flat">Flat — one rate set for all states & ages</option>
          <option value="dimensioned">By state / issue-age / product (rate matrix)</option>
        </select>
      </label>

      {draft.ancMode === 'flat' ? (
        <>
          <div className="text-[11px] font-medium text-neutral-500">Policy-year rate bands</div>
          <YearRateRows
            rows={draft.yearRates}
            onChange={(rows) => setDraft((d) => ({ ...d, yearRates: rows }))}
          />
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] text-neutral-400">
            Each cell applies only to records matching its state, issue-age, and product (blank =
            any). Most specific match wins; rows matching no cell are left unevaluated.
          </p>
          {draft.bands.map((b, i) => (
            <div key={b.uid} className="rounded-lg border border-neutral-200 p-3">
              <div className="mb-2 grid items-end gap-2 sm:grid-cols-[0.7fr_1fr_1.6fr_auto]">
                <label className="text-xs">
                  <span className="mb-1 block text-neutral-500">State</span>
                  <input
                    className={inp}
                    value={b.state}
                    placeholder="any"
                    maxLength={2}
                    onChange={(e) => setBand(i, { state: e.target.value.toUpperCase() })}
                  />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block text-neutral-500">Issue age</span>
                  <select
                    className={inp}
                    value={b.ageBand}
                    onChange={(e) => setBand(i, { ageBand: e.target.value as RateBand['ageBand'] })}
                  >
                    <option value="">Any age</option>
                    <option value="under_65">Under 65</option>
                    <option value="65_plus">65+</option>
                  </select>
                </label>
                <label className="text-xs">
                  <span className="mb-1 block text-neutral-500">
                    Product match (keywords/regex)
                  </span>
                  <input
                    className={inp}
                    value={b.planPattern}
                    placeholder="any — e.g. medicare supplement|med supp"
                    onChange={(e) => setBand(i, { planPattern: e.target.value })}
                  />
                </label>
                <div className="pb-0.5">
                  <button
                    type="button"
                    className={smallBtn}
                    disabled={draft.bands.length <= 1}
                    onClick={() =>
                      setDraft((d) => ({ ...d, bands: d.bands.filter((_, j) => j !== i) }))
                    }
                  >
                    Remove cell
                  </button>
                </div>
              </div>
              <YearRateRows
                rows={b.yearRates}
                onChange={(rows) => setBand(i, { yearRates: rows })}
              />
            </div>
          ))}
          <button
            type="button"
            className={smallBtn}
            onClick={() => setDraft((d) => ({ ...d, bands: [...d.bands, bandRow()] }))}
          >
            + Add rate cell
          </button>
          {anyAge && (
            <label className="block text-xs">
              <span className="mb-1 block text-neutral-500">
                Under-65 classifier — plan-name regex (matched = under 65)
              </span>
              <input
                className={inp}
                value={draft.under65Pattern}
                placeholder="\bDIS\b|DISABILITY"
                onChange={(e) => setDraft((d) => ({ ...d, under65Pattern: e.target.value }))}
              />
            </label>
          )}
          {anyAge && (
            <p className="text-[11px] text-neutral-400">
              Member DOB (age at effective date) is used when present; otherwise this plan-name
              pattern decides — e.g. disability Med-Supp plans carrying “DIS”.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
